import { useCallback, useEffect, useState } from "react"
import { TwoThumbSlider } from "../../icons/TODO-put-in-package/TwoThumbSlider"
import {
    findBinForValue,
    MAX_HEIGHT,
    MIN_HEIGHT,
    NUMBER_OF_BARS,
    temperatureColors,
} from "./module"
import * as Icons from "@nova/component-icons"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { InspectorPanelHotspotsLayerFiltersNotEnoughData } from "./InspectorPanelHotspotsLayerFiltersNotEnoughData"
import { useAppSelector } from "integrations/redux/hooks"
import { InfraredFlight } from "@nova/protobuf"
import { selectHotspotsThermalGraphByFlightId } from "features/entity-caching/hotspots/selectors"
import { InfraredFlightUpdates } from "../InspectorPanelHotspotLayer"

export const InspectorPanelHotspotLayerFiltersTemperature = ({
    updateFn,
    flight,
}: {
    updateFn: (updates: InfraredFlightUpdates) => void
    flight: InfraredFlight
}) => {
    // These refer to the slider values on the graph.
    const [selectedLowerBoundBin, setSelectedLowerBoundBin] = useState(0)
    const [selectedUpperBoundBin, setSelectedUpperBoundBin] = useState(NUMBER_OF_BARS)

    const {
        numberOfHotspotsPerBin,
        // leastHotspotsPerBar,
        mostHotspotsPerBin,
        barBounds,
        absoluteGraphMinimum,
        absoluteGraphMaximum,
    } = useAppSelector(selectHotspotsThermalGraphByFlightId(flight.id))

    // After the context of the graph changes. The slider values need to be updated accordingly.
    useEffect(() => {
        const flightMin = flight?.filters?.temperature?.min
        const flightMax = flight?.filters?.temperature?.max

        const selectedLowerBoundBin =
            flightMin !== undefined
                ? Math.min(
                      Math.max(findBinForValue({ value: flightMin, barBounds }), 0),
                      NUMBER_OF_BARS - 1
                  )
                : 0
        const selectedUpperBoundBin =
            flightMax !== undefined
                ? Math.min(
                      Math.max(findBinForValue({ value: flightMax, barBounds }), 1),
                      NUMBER_OF_BARS
                  )
                : NUMBER_OF_BARS

        setSelectedLowerBoundBin(selectedLowerBoundBin)
        setSelectedUpperBoundBin(selectedUpperBoundBin)
    }, [barBounds, absoluteGraphMinimum, absoluteGraphMaximum, flight?.filters?.temperature])

    const updateSelectedRange = useCallback(
        ({ lowerBoundBin, upperBoundBin }: { lowerBoundBin?: number; upperBoundBin?: number }) => {
            if (!flight) return

            const updates = {
                filters: {
                    ...flight.filters,
                    temperature: {
                        min:
                            lowerBoundBin !== undefined
                                ? lowerBoundBin !== 0
                                    ? barBounds[lowerBoundBin]
                                    : undefined
                                : flight?.filters?.temperature?.min,
                        max:
                            upperBoundBin !== undefined
                                ? upperBoundBin !== NUMBER_OF_BARS
                                    ? barBounds[upperBoundBin]
                                    : undefined
                                : flight?.filters?.temperature?.max,
                    },
                },
            }

            // if (isBatchUpdate && hotspotFlightIds) {
            //     debouncedDispatchHotspotUpdateActionForAllMergedFlights(
            //         updateInfraredFlight,
            //         hotspotFlightIds,
            //         updates
            //     )
            // } else
            // debounceDispatch(
            //     updateInfraredFlight({
            //         id: flight.id,
            //         updates,
            //     })
            // )
            updateFn(updates)

            if (lowerBoundBin !== undefined) setSelectedLowerBoundBin(lowerBoundBin)
            if (upperBoundBin !== undefined) setSelectedUpperBoundBin(upperBoundBin)
        },
        [flight, barBounds, updateFn]
    )

    const calculateBarHeight = useCallback(
        (i: number) => {
            const numberOfHotspots = numberOfHotspotsPerBin[i]
            const percentage = mostHotspotsPerBin > 0 ? numberOfHotspots / mostHotspotsPerBin : 0
            return percentage * (MAX_HEIGHT - MIN_HEIGHT)
        },
        [numberOfHotspotsPerBin, mostHotspotsPerBin]
    )

    const color = (i: number) => {
        if (i >= selectedLowerBoundBin && i < selectedUpperBoundBin) return temperatureColors[i]
        return "#FFFFFF1A" // white-200
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
                {/* Beta Tag */}
                <div className="px-2 bg-yellow-940 border border-white-200 rounded-full text-white-500 self-start tracking-widest select-none">
                    <small>BETA</small>
                </div>

                {/* Learn More Button */}
                {/* <small className="text-gray-500 hover:text-gray-300 cursor-pointer select-none">
                    Learn More
                </small> */}
            </div>

            {/* Title and Tooltip */}
            <div className="flex gap-1">
                <h5 className="text-gray-0 select-none">Temperature</h5>
                <div className="relative group/tooltip">
                    <Icons.SmallIcon
                        Icon={Icons.Information}
                        className="text-gray-300 hover:text-gray-0"
                    />
                    <Tooltip
                        title="Temperature accuracy varies by altitude, environment, and sensor. Please use discretion."
                        textClass="text-gray-0 text-center"
                        maxWidth={188}
                    />
                </div>
            </div>

            {absoluteGraphMaximum <= absoluteGraphMinimum ? (
                <InspectorPanelHotspotsLayerFiltersNotEnoughData />
            ) : (
                <>
                    {/* Graph */}
                    <div className="relative mx-[0.4375rem]">
                        <svg className="mx-[0.4375rem]" viewBox={`0 0 190 ${MAX_HEIGHT}`}>
                            {Array(NUMBER_OF_BARS)
                                .fill(null)
                                .map((_, i) => (
                                    <path
                                        key={i}
                                        d={`
                    M ${i * 6} ${MAX_HEIGHT - calculateBarHeight(i)}
                    a 2 2 0 0 1 4 0
                    v ${calculateBarHeight(i)}
                    h -4
                    z
                `}
                                        fill={color(i)}
                                    />
                                ))}
                        </svg>

                        <TwoThumbSlider
                            min={0}
                            max={NUMBER_OF_BARS}
                            selectedMin={selectedLowerBoundBin}
                            selectedMax={selectedUpperBoundBin}
                            setSelectedMin={(lowerBoundBin) =>
                                updateSelectedRange({ lowerBoundBin })
                            }
                            setSelectedMax={(upperBoundBin) =>
                                updateSelectedRange({ upperBoundBin })
                            }
                            isDisabled={barBounds.at(-1) === 0}
                        />
                    </div>

                    <div className="flex items-center justify-between text-gray-100 mx-[0.4375rem]">
                        <small className="mono">Cool</small>
                        <small className="mono">Hot</small>
                    </div>
                </>
            )}
        </div>
    )
}
